import { MAX_NOTE_LINKS, type DemoScope, type NoteLink, type NoteLinkInput, type NoteSide, type NoteTeam } from "@69ly/shared";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { Icon } from "../components/Icon.tsx";
import { Select } from "../components/Select.tsx";
import { useMatches } from "../lib/demo-queries.ts";
import { isMapId, mapInfo } from "@69ly/maps";

const mapName = (map: string) => (isMapId(map) ? mapInfo(map).name : map);

/**
 * What a note is about and where it points, shared by a new note and one being changed:
 * the side (T or CT), one of the demo's two teams, and links to other matches' maps and rounds.
 */
export interface NoteAbout {
  side: NoteSide | null;
  team: 0 | 1 | null;
  links: NoteLinkInput[];
}
export const NO_ABOUT: NoteAbout = { side: null, team: null, links: [] };

/** A link as it opens: a round of a map, a map, or the match. */
export function linkUrl(l: { matchId: string; demoId: string | null; round: number | null }): string {
  if (!l.demoId) return `/matches/${l.matchId}`;
  return l.round === null ? `/demos/${l.demoId}` : `/demos/${l.demoId}?r=${l.round + 1}`;
}

/** Side and team pickers, and the links. `teamNames`: the demo's teams 0 and 1 as the match names them. */
export function NoteAboutFields({ about, onChange, teamNames, scope }: { about: NoteAbout; onChange: (a: NoteAbout) => void; teamNames: [string, string]; scope: DemoScope | null }) {
  const { t } = useTranslation();
  return (
    <div className="note-about">
      <div className="note-about-row">
        <span className="note-about-label" id="note-side">
          {t("notes.side")}
        </span>
        <div className="segmented segmented-small" role="group" aria-labelledby="note-side">
          {([null, "t", "ct"] as const).map((s) => (
            <button key={s ?? "none"} type="button" className={`seg${s ? ` side-seg-${s}` : ""}`} aria-pressed={about.side === s} onClick={() => onChange({ ...about, side: s })}>
              {s ? t(`live.sideShort.${s}`) : t("notes.any")}
            </button>
          ))}
        </div>
      </div>
      <div className="note-about-row">
        <span className="note-about-label" id="note-team">
          {t("notes.team")}
        </span>
        <div className="segmented segmented-small note-team-seg" role="group" aria-labelledby="note-team">
          {([null, 0, 1] as const).map((k) => (
            <button key={k ?? "none"} type="button" className="seg" aria-pressed={about.team === k} onClick={() => onChange({ ...about, team: k })}>
              {k === null ? t("notes.any") : teamNames[k]}
            </button>
          ))}
        </div>
      </div>
      <LinksEditor links={about.links} onChange={(links) => onChange({ ...about, links })} scope={scope} />
    </div>
  );
}

/** Links to other matches: chosen match, then optionally a map and a round. */
function LinksEditor({ links, onChange, scope }: { links: NoteLinkInput[]; onChange: (l: NoteLinkInput[]) => void; scope: DemoScope | null }) {
  const { t } = useTranslation();
  const matches = useMatches(scope).data ?? [];
  const [open, setOpen] = useState(false);
  const [matchId, setMatchId] = useState<string | null>(null);
  const [demoId, setDemoId] = useState<string | null>(null);
  const [round, setRound] = useState("");
  const match = matches.find((m) => m.id === matchId) ?? null;
  const map = match?.maps.find((m) => m.demoId === demoId) ?? null;
  const roundNumber = Number(round);
  const roundOk = round === "" || (Number.isInteger(roundNumber) && roundNumber >= 1 && (!map || roundNumber <= map.rounds));
  const reset = () => {
    setOpen(false);
    setMatchId(null);
    setDemoId(null);
    setRound("");
  };
  const describe = (l: NoteLinkInput) => {
    const m = matches.find((x) => x.id === l.matchId);
    const mp = m?.maps.find((x) => x.demoId === l.demoId);
    return [m?.title ?? t("notes.aMatch"), mp ? mapName(mp.map) : null, l.round !== null && l.round !== undefined ? t("notes.roundN", { n: l.round + 1 }) : null].filter(Boolean).join(" · ");
  };
  return (
    <div className="note-links-edit">
      {links.length > 0 && (
        <ul className="note-links" aria-label={t("notes.links")}>
          {links.map((l, i) => (
            <li key={`${l.matchId}-${l.demoId}-${l.round}-${i}`} className="chip chip-small">
              <Icon name="link" size={12} />
              <span>{describe(l)}</span>
              <button type="button" className="chip-remove" aria-label={t("notes.removeLink", { link: describe(l) })} onClick={() => onChange(links.filter((_, j) => j !== i))}>
                <Icon name="close" size={12} />
              </button>
            </li>
          ))}
        </ul>
      )}
      {!open && links.length < MAX_NOTE_LINKS && (
        <button type="button" className="btn btn-quiet btn-small" onClick={() => setOpen(true)}>
          <Icon name="link" size={14} />
          {t("notes.addLink")}
        </button>
      )}
      {open && (
        <div className="note-link-picker" role="group" aria-label={t("notes.addLink")}>
          <Select
            label={t("notes.linkMatch")}
            value={matchId}
            placeholder={t("notes.linkMatch")}
            groups={[{ options: matches.map((m) => ({ value: m.id, label: m.title, hint: m.maps.map((x) => mapName(x.map)).join(", ") })) }]}
            onChange={(v) => {
              setMatchId(v);
              setDemoId(null);
              setRound("");
            }}
          />
          {match && (
            <Select
              label={t("notes.linkMap")}
              value={demoId ?? ""}
              placeholder={t("notes.wholeMatch")}
              groups={[
                {
                  options: [{ value: "", label: t("notes.wholeMatch") }, ...match.maps.map((m) => ({ value: m.demoId, label: `${m.number}. ${mapName(m.map)}`, hint: `${m.score[0]}–${m.score[1]}` }))],
                },
              ]}
              onChange={(v) => {
                setDemoId(v || null);
                setRound("");
              }}
            />
          )}
          {map && (
            <label className="field note-link-round">
              <span>{t("notes.linkRound", { of: map.rounds })}</span>
              <input type="number" inputMode="numeric" min={1} max={map.rounds} value={round} placeholder={t("notes.anyRound")} onChange={(e) => setRound(e.target.value)} aria-invalid={!roundOk} />
            </label>
          )}
          <div className="row">
            <button
              type="button"
              className="btn btn-secondary btn-small"
              disabled={!matchId || !roundOk}
              onClick={() => {
                onChange([...links, { matchId: matchId!, demoId, round: map && round !== "" ? roundNumber - 1 : null }]);
                reset();
              }}
            >
              {t("notes.linkAdd")}
            </button>
            <button type="button" className="btn btn-quiet btn-small" onClick={reset}>
              {t("common.cancel")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/** A note's side, team and links, as small tags (the links open where they point). */
export function NoteTags({ side, team, links }: { side: NoteSide | null; team: NoteTeam | null; links: NoteLink[] }) {
  const { t } = useTranslation();
  if (!side && !team && links.length === 0) return null;
  return (
    <span className="note-tags">
      {side && <span className={`live-side side-${side}`}>{t(`live.sideShort.${side}`)}</span>}
      {team && (
        <span className="chip chip-small note-team" title={t("notes.aboutTeam", { team: team.name })}>
          {team.name}
        </span>
      )}
      {links.map((l, i) =>
        l.missing ? (
          <span key={i} className="chip chip-small muted" title={t("notes.linkGone")}>
            <Icon name="link" size={12} />
            {t("notes.linkGone")}
          </span>
        ) : (
          <Link key={i} to={linkUrl(l)} className="chip chip-small note-link" onClick={(e) => e.stopPropagation()}>
            <Icon name="link" size={12} />
            {[l.matchTitle, l.map ? mapName(l.map) : null, l.round !== null ? t("notes.roundN", { n: l.round + 1 }) : null].filter(Boolean).join(" · ")}
          </Link>
        ),
      )}
    </span>
  );
}

/** The links a note holds, as sent back when it is changed. */
export const linkInputs = (links: NoteLink[]): NoteLinkInput[] => links.filter((l) => !l.missing).map((l) => ({ matchId: l.matchId, demoId: l.demoId, round: l.round }));
